import { Box, IconButton } from '@mui/material';
import HistoryIcon from '@mui/icons-material/History';
import CheckIcon from '@mui/icons-material/Check';
import { ICellRendererParams } from 'ag-grid-community';
import { useTranslation } from 'react-i18next';
import { formatNumber } from '../../utils/numberUtils';
import { VersionEntry } from './CorrectedTotalsHistory';

interface DayCell {
    date: string;
    total?: number | null;
    versions?: VersionEntry[] | null;
    lastEditedByRole?: 'TEF' | 'OSP' | null;
    isWorking?: boolean;
    comparisonValue?: number | null;
    isDraftOnly?: boolean; // True when cell shows draft value (no released exists)
    draftValue?: number | null;
    [key: string]: any;
}

interface CorrectedTotalsCellRendererProps extends ICellRendererParams {
    data: Record<string, DayCell>;
    onHistoryClick?: (cellData: DayCell, event: React.MouseEvent) => void;
    onApproveClick?: (cellData: DayCell, event: React.MouseEvent) => void;
    maxValue?: number; // Max value for gradient calculation (excludes draft-only values)
    canEdit?: boolean; // Whether user can edit/approve cells
    unsavedDates?: Set<string>; // Set of dates with unsaved changes
}

// Role colors - brighter for better visibility on dark backgrounds
const ROLE_COLORS = {
    OSP: '#FFA500', // Bright orange
    TEF: '#66BB6A', // Brighter green
};

export const CorrectedTotalsCellRenderer = (params: CorrectedTotalsCellRendererProps) => {
    const { t } = useTranslation();
    const colId = params.column?.getColId();
    if (!colId) return null;

    // For footer/aggregation rows, use the aggregated value directly. Read valueFormatted so the
    // screen agrees with what ag-grid puts on the clipboard and in the Excel export.
    if (params.node?.footer || params.node?.rowPinned) {
        if (params.value == null) return null;
        return <span>{params.valueFormatted}</span>;
    }

    if (!params.data) return null;

    const cellData = params.data[colId];
    const hasVersions = cellData?.versions && cellData.versions.length > 0;
    const value = cellData?.total;
    const role = cellData?.lastEditedByRole;
    const isDraftOnly = cellData?.isDraftOnly === true;
    const maxValue = params.maxValue || 0;
    // Check if there's a difference between released and unreleased values
    const hasComparisonDiff = cellData?.comparisonValue != null;
    // Check if this cell has unsaved changes
    const isUnsaved = cellData?.date && params.unsavedDates?.has(cellData.date);

    // For empty cells, show nothing
    if (value === null || value === undefined) {
        return null;
    }

    const handleHistoryClick = (e: React.MouseEvent) => {
        e.stopPropagation(); // Prevent cell editing
        if (params.onHistoryClick && cellData) {
            params.onHistoryClick(cellData, e);
        }
    };

    const handleApproveClick = (e: React.MouseEvent) => {
        e.stopPropagation(); // Prevent cell editing
        if (params.onApproveClick && cellData) {
            params.onApproveClick(cellData, e);
        }
    };

    // Show approve button only for TEF users (canEdit) and draft-only cells that haven't been approved yet
    // Once approved (added to unsavedDates), hide the checkmark
    // Also hide for non-working cells with zero/undefined value
    const isNonWorkingEmpty = !cellData?.isWorking && !value;
    const showApproveButton = params.canEdit && isDraftOnly && !isUnsaved && !isNonWorkingEmpty;

    // Calculate percentage for background gradient (only for released cells)
    let percentage = 0;
    if (!isDraftOnly && maxValue > 0) {
        percentage = (parseInt(String(value), 10) || 0) / maxValue;
        percentage = percentage < 0 ? 0 : percentage > 1 ? 1 : percentage;
    }

    // Build styles
    const containerStyles: any = {
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
        margin: '0 -4px',
        padding: '0 4px',
        // Priority: Unsaved > Draft-only > Released
        // Always set opacity: 1 to override any parent cell styling
        opacity: 1,
        ...(isUnsaved
            ? {
                  // Unsaved changes: yellow/gold background to indicate pending save
                  background: 'rgba(255, 193, 7, 0.5)', // MUI warning color
                  boxShadow: 'inset 0 0 0 2px rgba(255, 193, 7, 0.9)',
              }
            : isDraftOnly
              ? {
                    // Draft-only cells: soft gray background with reduced opacity text
                    background: 'rgba(0, 0, 0, 0.1)',
                    color: 'rgba(0, 0, 0, 0.4)',
                }
              : {
                    // Released cells: gradient from white to blue based on value
                    background: `linear-gradient(0deg, rgba(0, 102, 255, ${percentage}) 0%, rgba(0, 102, 255, ${percentage}) 100%), #fff`,
                }),
    };

    // Don't show role triangle for draft-only cells (it's obvious it's OSP draft)
    const roleColor = !isDraftOnly && role ? ROLE_COLORS[role] : null;

    return (
        <Box sx={containerStyles}>
            <span>{formatNumber(value)}</span>
            {/* Role indicator triangle in top-right corner - not shown for draft-only */}
            {roleColor && (
                <Box
                    sx={{
                        position: 'absolute',
                        top: -1,
                        right: -1,
                        width: 0,
                        height: 0,
                        zIndex: 1,
                        borderStyle: 'solid',
                        borderWidth: '0 12px 12px 0',
                        borderColor: `transparent ${roleColor} transparent transparent`,
                        filter: 'drop-shadow(-1px 1px 1px rgba(0,0,0,0.3))',
                    }}
                />
            )}
            {/* Approve button - checkmark to approve draft-only value (replaces history button for draft-only) */}
            {showApproveButton && (
                <IconButton
                    size="small"
                    onClick={handleApproveClick}
                    sx={{
                        position: 'absolute',
                        top: 0,
                        right: 0,
                        height: '100%',
                        bottom: 0,
                        padding: '0 6px',
                        borderRadius: 0,
                        backgroundColor: 'rgba(102, 187, 106, 0.6)', // Green background
                        '&:hover': {
                            backgroundColor: 'rgba(102, 187, 106, 0.8)',
                        },
                    }}
                    title={t('bidding.approveDraft')}
                >
                    <CheckIcon
                        sx={{
                            fontSize: 14,
                            color: '#FFF',
                        }}
                    />
                </IconButton>
            )}
            {/* History button - shown for cells with history or comparison diff (but NOT for unapproved draft-only) */}
            {!showApproveButton && (hasVersions || hasComparisonDiff) && (
                <IconButton
                    size="small"
                    onClick={handleHistoryClick}
                    sx={{
                        position: 'absolute',
                        top: 0,
                        right: 0,
                        height: '100%',
                        bottom: 0,
                        padding: '0 0 0 6px',
                        borderRadius: 0,
                        backgroundColor: hasComparisonDiff
                            ? 'rgb(243,212,118)' // yellow for draft differences
                            : 'rgba(255, 255, 255, 0.5)',
                        '&:hover': {
                            backgroundColor: hasComparisonDiff ? 'rgb(236,192,65)' : 'rgba(255, 255, 255, .75)',
                        },
                    }}
                    title={
                        hasComparisonDiff
                            ? t('bidding.draftValueDiffers', { value: cellData.comparisonValue })
                            : t('hint.viewEditHistory')
                    }
                >
                    <HistoryIcon
                        sx={{
                            fontSize: 14,
                            marginRight: 1,
                        }}
                    />
                </IconButton>
            )}
        </Box>
    );
};

export default CorrectedTotalsCellRenderer;
